import styled, { css } from 'styled-components';

import colors from '@front/common/src/utils/colors';
import { toRem } from '@front/common/src/utils/styles';
import { ButtonSize, ButtonType } from '@front/common/src/types/components';
import {
  BORDER_WIDTH,
  HEIGHT_BY_SIZE,
  PADDING_HORIZONTAL_BY_SIZE,
} from '@front/common/src/components/button/constants';

import { TextVariantProps } from '../base-text/variants/type';
import Text from '../base-text/variants/text';
import { getGradient } from '../../styles/gradient';

export const BUTTON_TEXT_BY_SIZE: Record<ButtonSize, React.FC<TextVariantProps>> = {
  lg: Text.ButtonLG,
  md: Text.ButtonMD,
  sm: Text.ButtonSM,
};

export const getColors = (type: ButtonType, isLoading: boolean) => {
  let backgroundColor;
  let color;
  let borderColor;
  let iconColor;
  let loadingSpinnerColor;

  switch (type) {
    case 'secondary':
      color = colors.ink100;
      backgroundColor = colors.transparent;
      borderColor = colors.ink100;
      iconColor = colors.ink100;
      loadingSpinnerColor = colors.ink100;
      break;

    case 'black':
      color = colors.white100;
      backgroundColor = colors.ink100;
      borderColor = colors.ink100;
      iconColor = colors.white100;
      loadingSpinnerColor = colors.white100;
      break;

    case 'success':
      color = colors.white100;
      backgroundColor = colors.success100;
      borderColor = colors.success100;
      iconColor = colors.white100;
      loadingSpinnerColor = colors.white100;
      break;

    case 'danger':
      color = colors.white100;
      backgroundColor = colors.error100;
      borderColor = colors.error100;
      iconColor = colors.white100;
      loadingSpinnerColor = colors.white100;
      break;

    default:
      color = colors.white100;
      backgroundColor = getGradient();
      borderColor = colors.transparent;
      iconColor = colors.white100;
      loadingSpinnerColor = colors.white100;
  }

  if (isLoading) {
    color = colors.transparent;
    iconColor = colors.transparent;
  }

  return {
    color,
    backgroundColor,
    borderColor,
    iconColor,
    loadingSpinnerColor,
  };
};

export const StyledLoadingWrapper = styled.span`
  position: absolute;
`;

export const StyledIconWrapper = styled.span`
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-right: ${toRem(7)};
  height: auto;
`;

const disabledStyle = css`
  cursor: not-allowed;
  opacity: 0.5;
`;

const variantStyles = (isDisabled: boolean, backgroundColor: string, type: ButtonType = 'primary') =>
  ({
    primary: css`
      &:hover {
        transition: all 0.3s ease-out;
        text-decoration: none;
        background: ${!isDisabled ? getGradient('gradient110') : backgroundColor};
      }

      &:active {
        transition: all 0.3s ease-out;
        text-decoration: none;
        background: ${!isDisabled ? getGradient('gradient120') : backgroundColor};
      }
    `,
    secondary: css`
      &:hover {
        background: ${isDisabled ? backgroundColor : colors.ink10};
      }

      &:active {
        background-color: ${isDisabled ? backgroundColor : colors.ink20};
      }
    `,
    black: css`
      &:hover {
        background-color: ${isDisabled ? backgroundColor : colors.ink110};
      }

      &:active {
        background-color: ${isDisabled ? backgroundColor : colors.ink120};
      }
    `,
    success: css`
      /* for stylelint no-empty-source */
    `,
    danger: css`
      /* for stylelint no-empty-source */
    `,
  })[type];

export const StyledButton = styled.button<{
  $size: ButtonSize;
  $type: ButtonType;
  $borderColor: string;
  $isRound: boolean;
  $backgroundColor: string;
  $hasFullWidth: boolean;
  $isDisabled: boolean;
}>`
  position: relative;
  display: inline-flex;
  box-sizing: border-box;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  text-decoration: none;

  ${({ $isDisabled, $size, $backgroundColor, $borderColor, $isRound, $type, $hasFullWidth }) => `
    cursor: pointer;
    ${$isDisabled ? disabledStyle : ''}
    height: ${toRem(HEIGHT_BY_SIZE[$size])};
    padding: 0 ${toRem(PADDING_HORIZONTAL_BY_SIZE[$size])};
    border: ${toRem(BORDER_WIDTH)} solid ${$borderColor};
    border-radius: ${toRem($isRound ? 30 : 0)};
    background: ${$type === 'primary' ? getGradient() : $backgroundColor};
    width: ${$hasFullWidth ? '100%' : 'auto'};
  `}

  ${({ $type, $backgroundColor, $isDisabled }) => variantStyles($isDisabled, $backgroundColor, $type)}
`;
